import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { getFunnelVariant } from '../config/funnelV2';

interface UserData {
    em?: string;
    fn?: string;
    ln?: string;
    external_id?: string;
}

function pushDataLayer(payload: Record<string, unknown>): void {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        funnel_variant: getFunnelVariant(),
        ...payload,
    });
}

interface GTMContextType {
    trackEvent: (eventName: string, eventData?: Record<string, any>) => void;
    trackPageView: (pagePath: string, pageTitle?: string) => void;
    trackLead: (leadData?: Record<string, any>) => void;
    trackPurchase: (purchaseData: {
        transactionId: string;
        value: number;
        currency?: string;
        items?: any[];
        user_data?: UserData;
        [key: string]: any;
    }) => void;
    setUserId: (userId: string) => void;
    setUserProperties: (properties: Record<string, any>) => void;
}

const GTMContext = createContext<GTMContextType | undefined>(undefined);

interface GTMProviderProps {
    gtmId: string;
    children: ReactNode;
}

declare global {
    interface Window {
        dataLayer: any[];
    }
}

// Generate unique event ID for deduplication
const generateEventId = (prefix: string, transactionId?: string): string => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8);
    return transactionId  
        ? `${prefix}_${transactionId}_${timestamp}` 
        : `${prefix}_${timestamp}_${random}`;
};

export const GTMProvider: React.FC<GTMProviderProps> = ({ gtmId, children }) => {
    useEffect(() => {
        // Initialize dataLayer if it doesn't exist
        window.dataLayer = window.dataLayer || [];

        // Add GTM script to the page
        const script = document.createElement('script');
        script.innerHTML = `
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${gtmId}');
        `;
        document.head.appendChild(script);

        // Add noscript iframe
        const noscript = document.createElement('noscript');
        const iframe = document.createElement('iframe');
        iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
        iframe.height = '0';
        iframe.width = '0';
        iframe.style.display = 'none';
        iframe.style.visibility = 'hidden';
        noscript.appendChild(iframe);
        document.body.insertBefore(noscript, document.body.firstChild);

        return () => {
            // Cleanup on unmount (optional)
            document.head.removeChild(script);
            if (noscript.parentNode) {
                document.body.removeChild(noscript);
            }
        };
    }, [gtmId]);

    const trackEvent = (eventName: string, eventData?: Record<string, any>) => {
        console.log('GTM Event:', eventName, eventData);
        pushDataLayer({
            event: eventName,
            ...eventData,
        });
    };

    const trackPageView = (pagePath: string, pageTitle?: string) => {
        console.log('GTM Page View:', pagePath);
        pushDataLayer({
            event: 'pageview',
            page: {
                path: pagePath,
                title: pageTitle || document.title,
            },
        });
    };

    const trackLead = (leadData?: Record<string, any>) => {
        console.log('GTM Lead:', leadData);
        pushDataLayer({
            event: 'lead',
            event_id: generateEventId('lead'),
            ...leadData
        });
    };

    const trackPurchase = (purchaseData: {
        transactionId: string;
        value: number;
        currency?: string;
        items?: any[];
        user_data?: UserData;
        [key: string]: any;
    }) => {
        console.log('GTM Purchase:', purchaseData);

        // Unique event_id for Pixel + CAPI deduplication
        const eventId = generateEventId('purchase', purchaseData.transactionId);

        pushDataLayer({
            event: 'purchase',
            event_id: eventId,
            ecommerce: {
                transaction_id: purchaseData.transactionId,
                value: purchaseData.value,
                currency: purchaseData.currency || 'USD',
                items: purchaseData.items || [],
            },
            user_data: purchaseData.user_data ? {
                em: purchaseData.user_data.em?.toLowerCase().trim(),
                fn: purchaseData.user_data.fn?.toLowerCase().trim(),
                ln: purchaseData.user_data.ln?.toLowerCase().trim(),
                external_id: purchaseData.user_data.external_id,
            } : undefined,
        });
    };

    const setUserId = (userId: string) => {
        console.log('GTM Set User ID:', userId);
        pushDataLayer({
            event: 'user_id_set',
            userId: userId,
        });
    };

    const setUserProperties = (properties: Record<string, any>) => {
        console.log('GTM Set User Properties:', properties);
        pushDataLayer({
            event: 'user_properties_set',
            userProperties: properties,
        });
    };

    return (
        <GTMContext.Provider
            value={{
                trackEvent,
                trackPageView,
                trackLead,
                trackPurchase,
                setUserId,
                setUserProperties,
            }}
        >
            {children}
        </GTMContext.Provider>
    );
};

// No-op fallback when GTM is unavailable
const noopGTM: GTMContextType = {
    trackEvent: () => {},
    trackPageView: () => {},
    trackLead: () => {},
    trackPurchase: () => {},
    setUserId: () => {},
    setUserProperties: () => {},
};

// Custom hook to use GTM — returns no-ops if provider is unavailable
export const useGTM = (): GTMContextType => {
    const context = useContext(GTMContext);
    return context ?? noopGTM;
};